import React, { useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Paper,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Select from "react-select";
import { components } from "react-select";
import Loader from "utils/Loader";
import NonBilledReportsController from "pages/Controller/NonBilledReportsController";

const selectStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  control: (base) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderColor: "#d0d7de",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#b7c1cc"
    }
  }),
  valueContainer: (base) => ({
    ...base,
    height: 40,
    padding: "0 8px"
  }),
  placeholder: (base) => ({
    ...base,
    color: "#111",
    fontWeight: 500
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 6,
    color: "#000",
    cursor: "pointer"
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: "block"
  })
};

const ClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <ClearIcon sx={{ fontSize: 18, color: "#000" }} />
  </components.ClearIndicator>
);

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const formatDisplayDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getCustomerDisplay = (row) => {
  const code = row?.customer_code || "-";
  const name = row?.partyName || row?.customer_name || "-";
  return `${name} - ${code}`;
};

const getDivisionDisplay = (row) => {
  const code = row?.dv || row?.division || "-";
  const name = row?.division_name || row?.div_name || row?.division || "-";
  return `${code} - ${name}`;
};

const summaryItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  flexWrap: "nowrap",
  justifyContent: "center",
  textAlign: "center"
};

const summaryLabelStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: "#8a1c1c",
  textTransform: "uppercase",
  letterSpacing: 0.3
};

const summaryValueStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "#1f2933"
};

const detailCellSx = {
  py: 0.7,
  fontSize: 12,
  textAlign: "center",
  whiteSpace: "nowrap"
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const NonBilledReports = () => {
  const {
    filters,
    customerOptions,
    divisionOptions,
    reportData,
    grandTotal,
    cloading,
    tableLoading,
    dropdownLoading,
    handleFilterChange,
    handleDateChange,
    handleCustomerSearch,
    loadAllCustomers,
    isFromApp
  } = NonBilledReportsController();

  const [expandedRows, setExpandedRows] = useState({});
  const customerSelectOptions = useMemo(() => customerOptions, [customerOptions]);
  const divisionSelectOptions = useMemo(() => divisionOptions, [divisionOptions]);

  const toggleRow = (deliveryNo) => {
    setExpandedRows((prev) => ({
      ...prev,
      [deliveryNo]: !prev[deliveryNo]
    }));
  };

  const resetExpandedRows = () => {
    setExpandedRows({});
  };

  const handleExcelDownload = () => {
    const rowsHtml = reportData
      .map((row) => {
        const detailRows = (row.details || [])
          .map(
            (detail) => `
            <tr>
              <td>${escapeHtml(row.delivery_no || "-")}</td>
              <td>${escapeHtml(formatDisplayDate(row.delivery_date))}</td>
              <td>${escapeHtml(row.customer_code || "-")}</td>
              <td>${escapeHtml(row.customer_name || "-")}</td>
              <td>${escapeHtml(detail.material_code || "-")}</td>
              <td>${escapeHtml(detail.material_desc || "-")}</td>
              <td>${escapeHtml(detail.batch || "-")}</td>
              <td>${escapeHtml(row.dv || row.division || "-")}</td>
              <td>${escapeHtml(row.division_name || row.div_name || row.division || "-")}</td>
              <td>${escapeHtml(formatNumber(detail.quantity))}</td>
              <td>${escapeHtml(formatNumber(detail.size))}</td>
              <td>${escapeHtml(formatNumber(detail.meters))}</td>
            </tr>
          `
          )
          .join("");
        return detailRows;
      })
      .join("");

    const html = `
      <html>
        <head><meta charset="utf-8" /></head>
        <body>
          <table border="1">
            <tr>
              <th>Delivery</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Customer Name</th>
              <th>MaterialCode</th>
              <th>MaterialDesc</th>
              <th>Batch</th>
              <th>Div</th>
              <th>Div_name</th>
              <th>Quantity</th>
              <th>Size</th>
              <th>Meter</th>
            </tr>
            ${rowsHtml}
            <tr>
              <td colspan="9"><b>Grand Total</b></td>
              <td><b>${escapeHtml(formatNumber(grandTotal.total_rolls))}</b></td>
              <td>-</td>
              <td><b>${escapeHtml(formatNumber(grandTotal.total_meters))}</b></td>
            </tr>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob(["\ufeff", html], {
      type: "application/vnd.ms-excel;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `non_billed_report_${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3 },
        background: "#f4f6f8",
        minHeight: "100vh",
        mt: isFromApp ? 0 : 6,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box"
      }}
    >
      {cloading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.65)",
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1
          }}
        >
          <Loader />
          <Typography>Please Wait....</Typography>
        </Box>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography className="font20" sx={{ color: "#111", fontWeight:'bold' }}>
            NON BILLED REPORT
          </Typography>
        </Box>

        <Grid
          container
          columnSpacing={2}
          rowSpacing={2}
          sx={{
            width: "100%",
            margin: 0,
            alignItems: "flex-start"
          }}
        >
          <Grid item xs={12} sm={12} md={5} sx={{ minWidth: 0 }}>
            <Select
              options={customerSelectOptions}
              value={filters.customer}
              onChange={(value) => {
                resetExpandedRows();
                handleFilterChange("customer")(value);
              }}
              onInputChange={(value, { action }) => {
                if (action === "input-change" && value.length >= 3) {
                  handleCustomerSearch(value);
                }
                if (action === "menu-close" && !value) {
                  loadAllCustomers();
                }
              }}
              isSearchable
              isClearable
              isLoading={dropdownLoading}
              placeholder="Search Customer"
              components={{ ClearIndicator }}
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Select
              options={divisionSelectOptions}
              value={filters.division}
              onChange={(value) => {
                resetExpandedRows();
                handleFilterChange("division")(value);
              }}
              isSearchable
              isClearable
              isLoading={dropdownLoading}
              placeholder="Select Division"
              components={{ ClearIndicator }}
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </Grid>

          <Grid item xs={12} sm={3} md={2} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={filters.fromDate}
              onChange={(event) => {
                resetExpandedRows();
                handleDateChange("fromDate")(event);
              }}
              inputProps={{
                max: filters.toDate || undefined
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3} md={2} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={filters.toDate}
              onChange={(event) => {
                resetExpandedRows();
                handleDateChange("toDate")(event);
              }}
              inputProps={{
                min: filters.fromDate || undefined
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40
                }
              }}
              InputProps={{
                endAdornment: (filters.fromDate || filters.toDate) ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => {
                        resetExpandedRows();
                        handleDateChange("fromDate")({ target: { value: "" } });
                        handleDateChange("toDate")({ target: { value: "" } });
                      }}
                      sx={{ color: "#000" }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "#fff",
          position: "relative"
        }}
      >
        {tableLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.65)",
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              backdropFilter: "blur(1px)",
              transition: "opacity 180ms ease"
            }}
          >
            <Loader />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Loading...</Typography>
          </Box>
        )}
        <Box
          sx={{
            opacity: tableLoading ? 0.55 : 1,
            transform: tableLoading ? "translateY(2px)" : "translateY(0)",
            transition: "opacity 180ms ease, transform 180ms ease"
          }}
        >
        {reportData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e6ebf1",
              backgroundColor: "#fff"
            }}
          >
            <IconButton
              size="small"
              onClick={handleExcelDownload}
              sx={{
                color: "#d32f2f",
                border: "1px solid #e0e0e0",
                borderRadius: 1
              }}
              title="Download Excel"
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {reportData.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: "#5f6c7b", backgroundColor: "#fff" }}>
            No data found
          </Box>
        ) : (
          <>
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: "1px solid #e6ebf1",
                backgroundColor: "red",
                display: { xs: "none", md: "block" }
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "32px 1.1fr 0.9fr 2fr 0.8fr 0.7fr 0.7fr"
                  },
                  gap: 1.25
                }}
              >
                {["", "Delivery", "Date", "Customer", "Division", "Rolls", "Meters"].map((header, index) => (
                  <Box
                    key={`${header}-${index}`}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#ffffff",
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                      textAlign: "center"
                    }}
                  >
                    {header}
                  </Box>
                ))}
              </Box>
            </Box>

            {reportData.map((row) => {
              const isExpanded = Boolean(expandedRows[row.delivery_no]);

              return (
                <Box
                  key={row.delivery_no}
                  sx={{
                  borderTop: "1px solid #e6ebf1",
                    background: "#fff",
                    "&:hover": {
                      backgroundColor: "#fafafa"
                    }
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.85,
                      display: "grid",
                      gridTemplateColumns: "32px minmax(0, 1fr)",
                      gap: 1.25,
                      cursor: "pointer"
                    }}
                    onClick={() => toggleRow(row.delivery_no)}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleRow(row.delivery_no);
                      }}
                      sx={{ color: "#000" }}
                    >
                      {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>

                    <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "1.1fr 0.9fr 2fr 0.8fr 0.7fr 0.7fr"
                          },
                          gap: { xs: 0.5, md: 1.25 },
                          alignItems: "center",
                          textAlign: "center"
                        }}
                      >
                      <Box sx={{ ...summaryItemStyle, minWidth: 0, flexDirection: { xs: "row", md: "row" }, justifyContent: { xs: "flex-start", md: "center" } }}>
                        <Box component="span" sx={{ ...summaryLabelStyle, display: { xs: "inline", md: "none" } }}>Delivery</Box>
                        <Box component="span" sx={summaryValueStyle}>{row.delivery_no || "-"}</Box>
                      </Box>

                      <Box sx={{ ...summaryItemStyle, minWidth: 0, justifyContent: { xs: "flex-start", md: "center" } }}>
                        <Box component="span" sx={{ ...summaryLabelStyle, display: { xs: "inline", md: "none" } }}>Date</Box>
                        <Box component="span" sx={summaryValueStyle}>{formatDisplayDate(row.delivery_date)}</Box>
                      </Box>

                      <Box
                        sx={{
                          ...summaryItemStyle,
                          minWidth: 0,
                          justifyContent: { xs: "flex-start", md: "center" }
                        }}
                      >
                        <Box component="span" sx={{ ...summaryLabelStyle, display: { xs: "inline", md: "none" } }}>Customer</Box>
                        <Box
                          component="span"
                          sx={{
                            ...summaryValueStyle,
                            whiteSpace: { xs: "normal", md: "nowrap" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                            width: "100%",
                            textAlign: { xs: "left", md: "center" }
                          }}
                          title={row.customer_name || "-"}
                        >
                          {getCustomerDisplay(row)}
                        </Box>
                      </Box>

                      <Box sx={{ ...summaryItemStyle, minWidth: 0, justifyContent: { xs: "flex-start", md: "center" } }}>
                        <Box component="span" sx={{ ...summaryLabelStyle, display: { xs: "inline", md: "none" } }}>Division</Box>
                        <Box component="span" sx={summaryValueStyle}>{getDivisionDisplay(row)}</Box>
                      </Box>

                      <Box sx={{ ...summaryItemStyle, minWidth: 0, justifyContent: { xs: "flex-start", md: "center" } }}>
                        <Box component="span" sx={{ ...summaryLabelStyle, display: { xs: "inline", md: "none" } }}>Rolls</Box>
                        <Box component="span" sx={summaryValueStyle}>{formatNumber(row.total_rolls)}</Box>
                      </Box>

                      <Box sx={{ ...summaryItemStyle, minWidth: 0, justifyContent: { xs: "flex-start", md: "center" } }}>
                        <Box component="span" sx={{ ...summaryLabelStyle, display: { xs: "inline", md: "none" } }}>Meters</Box>
                        <Box component="span" sx={summaryValueStyle}>{formatNumber(row.total_meters)}</Box>
                      </Box>
                    </Box>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                          borderColor: "#f0c7c7",
                          backgroundColor: "#fff",
                          overflowX: "auto",
                          overflowY: "hidden"
                        }}
                      >
                        <Table
                          size="small"
                          sx={{
                            minWidth: { xs: 720, md: "100%" }
                          }}
                        >
                          <TableHead>
                            <TableRow sx={{ background: "#fff1f1" }}>
                              <TableCell align="center" sx={{ color: "#8a1c1c", py: 0.75, fontSize: 12, fontWeight: 700 }}>Material Code</TableCell>
                              <TableCell align="center" sx={{ color: "#8a1c1c", py: 0.75, fontSize: 12, fontWeight: 700 }}>Material Description</TableCell>
                              <TableCell align="center" sx={{ color: "#8a1c1c", py: 0.75, fontSize: 12, fontWeight: 700 }}>Batch</TableCell>
                              <TableCell align="center" sx={{ color: "#8a1c1c", py: 0.75, fontSize: 12, fontWeight: 700 }}>Quantity</TableCell>
                              <TableCell align="center" sx={{ color: "#8a1c1c", py: 0.75, fontSize: 12, fontWeight: 700 }}>Size</TableCell>
                              <TableCell align="center" sx={{ color: "#8a1c1c", py: 0.75, fontSize: 12, fontWeight: 700 }}>Meters</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(row.details || []).map((detail, index) => (
                              <TableRow
                                key={`${row.delivery_no}-${index}`}
                                sx={{
                                  "&:nth-of-type(even)": {
                                    backgroundColor: "#fcfcfc"
                                  }
                                }}
                              >
                                <TableCell align="center" sx={detailCellSx}>{detail.material_code || "-"}</TableCell>
                                <TableCell align="center" sx={detailCellSx}>{detail.material_desc || "-"}</TableCell>
                                <TableCell align="center" sx={detailCellSx}>{detail.batch || "-"}</TableCell>
                                <TableCell align="center" sx={detailCellSx}>{formatNumber(detail.quantity)}</TableCell>
                                <TableCell align="center" sx={detailCellSx}>{formatNumber(detail.size)}</TableCell>
                                <TableCell align="center" sx={detailCellSx}>{formatNumber(detail.meters)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ background: "#f5f5f5" }}>
                              <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, fontSize: 12 }} colSpan={3}>
                                Total
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, fontSize: 12 }}>
                                {formatNumber(row.total_rolls)}
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.75, fontSize: 12 }}>-</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, fontSize: 12 }}>
                                {formatNumber(row.total_meters)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </>
        )}

        {reportData.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: "1px solid #d8e1ea",
              background: "#f5f5f5",
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              flexWrap: "wrap",
              gap: { xs: 1.5, md: 4 },
              fontWeight: 700
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: 14, fontWeight: 700, textAlign: "center" }}
            >
              Grand Rolls: {formatNumber(grandTotal.total_rolls)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: 14, fontWeight: 700, textAlign: "center" }}
            >
              Grand Meters: {formatNumber(grandTotal.total_meters)}
            </Typography>
          </Box>
        )}
        </Box>
      </Paper>
    </Box>
  );
};

export default NonBilledReports;
